import React, { useState, useEffect } from 'react';
import './bottomBar.css';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend as RechartsLegend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';

const PROCESSING_TAB_OPTIONS = [
  { key: 'filters', label: 'Filters' },
  { key: 'summary', label: 'Summary' },
  { key: 'crusher', label: 'Crusher' },
  { key: 'mill1', label: 'Mill1' },
  { key: 'mill3', label: 'Mill3' },
  { key: 'mill4', label: 'Mill4' },
  { key: 'downtime', label: 'Downtime' },
  { key: 'goldproduced', label: 'Gold Produced' },
];

const ProcessingBottomBar = ({ expanded, onExpand, onHide }) => {
  const [selectedTab, setSelectedTab] = useState('filters');
  const [crushingData, setCrushingData] = useState([]);
  const [mill1Data, setMill1Data] = useState([]);
  const [mill3Data, setMill3Data] = useState([]);
  const [mill4Data, setMill4Data] = useState([]);
  const [goldProducedData, setGoldProducedData] = useState([]);
  const [downtimeData, setDowntimeData] = useState([]);
  const [commentsData, setCommentsData] = useState([]);
  const [millingPlanData, setMillingPlanData] = useState([]);
  const [loading, setLoading] = useState({
    crushing: false,
    mill1: false,
    mill3: false,
    mill4: false,
    goldProduced: false,
    downtime: false,
    comments: false,
    millingPlan: false
  });

  // Filter state
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [activeDateRange, setActiveDateRange] = useState('thisMonth');
  const [filteredData, setFilteredData] = useState({
    crushing: [],
    mill1: [],
    mill3: [],
    mill4: [],
    goldProduced: [],
    downtime: [],
    comments: []
  });

  // Collapsible sections state for downtime tab
  const [collapsedSections, setCollapsedSections] = useState({
    effectiveRunningTime: true,
    availability: true,
    utilization: true
  });

  // API Base URL
  const API_BASE_URL = 'https://pickstonepresentation.dumisaminerals.com/server/api';

  // Initialize default dates (this month)
  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setFromDate(formatDateForInput(firstDay));
    setToDate(formatDateForInput(lastDay));
  }, []);

  // Load all processing data on component mount
  useEffect(() => {
    loadAllProcessingData();
  }, []);

  // Apply filters when dates change
  useEffect(() => {
    applyFilters();
  }, [fromDate, toDate, crushingData, mill1Data, mill3Data, mill4Data, goldProducedData, downtimeData, commentsData,millingPlanData]);

  // Handle manual date input changes
  const handleDateChange = (type, value) => {
    if (type === 'fromDate') {
      setFromDate(value);
    } else if (type === 'toDate') {
      setToDate(value);
    }
    // Reset active date range when manually changing dates
    setActiveDateRange('custom');
  };

  const toggleSection = (section) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Date utility functions
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForChart = (date) => {
    const dateObj = new Date(date);
    const day = String(dateObj.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <div className="tooltip-date">{label}</div>
          {payload.map((entry, index) => (
            <div key={index} className="tooltip-item">
              <span className="tooltip-color" style={{ backgroundColor: entry.color }}></span>
              <span className="tooltip-label">{entry.name}:</span>
              <span className="tooltip-value">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const setDateRange = (type) => {
    const now = new Date();
    let startDate, endDate;

    switch (type) {
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = yesterday;
        break;
      case 'thisWeek':
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        startDate = startOfWeek;
        endDate = endOfWeek;
        break;
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'thisYear':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        return;
    }

    setActiveDateRange(type);
    setFromDate(formatDateForInput(startDate));
    setToDate(formatDateForInput(endDate));
  };

  const applyFilters = () => {
    if (!fromDate || !toDate) return;

    const filterData = (data) => {
      return data.filter(item => {
        const itemDate = new Date(item.DateReported);
        const from = new Date(fromDate);
        const to = new Date(toDate);
        return itemDate >= from && itemDate <= to;
      });
    };

    setFilteredData({
      crushing: filterData(crushingData),
      mill1: filterData(mill1Data),
      mill3: filterData(mill3Data),
      mill4: filterData(mill4Data),
      goldProduced: filterData(goldProducedData),
      downtime: filterData(downtimeData),
      comments: filterData(commentsData),
      millingPlan: filterData(millingPlanData)
    });
  };

  // Log data state changes
  useEffect(() => {
    console.log('ProcessingBottomBar: Data state updated:', {
      crushingData: crushingData.length,
      mill1Data: mill1Data.length,
      mill3Data: mill3Data.length,
      mill4Data: mill4Data.length,
      goldProducedData: goldProducedData.length,
      downtimeData: downtimeData.length,
      commentsData: commentsData.length,
      millingPlanData: millingPlanData.length
    });
  }, [crushingData, mill1Data, mill3Data, mill4Data, goldProducedData, downtimeData, commentsData, millingPlanData]);

  const loadAllProcessingData = async () => {
    console.log('ProcessingBottomBar: Starting to load all processing data...');
    await Promise.all([
      loadCrushingData(),
      loadMill1Data(),
      loadMill3Data(),
      loadMill4Data(),
      loadGoldProducedData(),
      loadDowntimeData(),
      loadCommentsData(),
      loadMillingPlanData()
    ]);
    console.log('ProcessingBottomBar: All processing data loading completed');
  };

  const loadCrushingData = async () => {
    setLoading(prev => ({ ...prev, crushing: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/processing_crushing`);
      const data = await response.json();
      console.log('ProcessingBottomBar: Crushing data loaded:', data);
      setCrushingData(data);
    } catch (error) {
      console.error('Error loading crushing data:', error);
    } finally {
      setLoading(prev => ({ ...prev, crushing: false }));
    }
  };

  const loadMill1Data = async () => {
    setLoading(prev => ({ ...prev, mill1: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/processing_mill1`);
      const data = await response.json();
      console.log('ProcessingBottomBar: Mill1 data loaded:', data);
      setMill1Data(data);
    } catch (error) {
      console.error('Error loading mill1 data:', error);
    } finally {
      setLoading(prev => ({ ...prev, mill1: false }));
    }
  };

  const loadMill4Data = async () => {
    setLoading(prev => ({ ...prev, mill4: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/processing_mill4`);
      const data = await response.json();
      console.log('ProcessingBottomBar: Mill4 data loaded:', data);
      setMill4Data(data);
    } catch (error) {
      console.error('Error loading mill4 data:', error);
    } finally {
      setLoading(prev => ({ ...prev, mill4: false }));
    }
  };

  const loadMill3Data = async () => {
    setLoading(prev => ({ ...prev, mill3: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/processing_mill3`);
      const data = await response.json();
      console.log('ProcessingBottomBar: Mill3 data loaded:', data);
      setMill3Data(data);
    } catch (error) {
      console.error('Error loading mill3 data:', error);
    } finally {
      setLoading(prev => ({ ...prev, mill3: false }));
    }
  };

  const loadGoldProducedData = async () => {
    setLoading(prev => ({ ...prev, goldProduced: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/processing_producedgold`);
      const data = await response.json();
      console.log('ProcessingBottomBar: Gold produced data loaded:', data);
      setGoldProducedData(data);
    } catch (error) {
      console.error('Error loading gold produced data:', error);
    } finally {
      setLoading(prev => ({ ...prev, goldProduced: false }));
    }
  };

  const loadDowntimeData = async () => {
    setLoading(prev => ({ ...prev, downtime: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/processing_downtime`);
      const data = await response.json();
      console.log('ProcessingBottomBar: Downtime data loaded:', data);
      setDowntimeData(data);
    } catch (error) {
      console.error('Error loading downtime data:', error);
    } finally {
      setLoading(prev => ({ ...prev, downtime: false }));
    }
  };

  const loadCommentsData = async () => {
    setLoading(prev => ({ ...prev, comments: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/processing_comments`);
      const data = await response.json();
      console.log('ProcessingBottomBar: Comments data loaded:', data);
      setCommentsData(data);
    } catch (error) {
      console.error('Error loading comments data:', error);
    } finally {
      setLoading(prev => ({ ...prev, comments: false }));
    }
  };

  const loadMillingPlanData = async () => {
    setLoading(prev => ({ ...prev, millingPlan: true }));
    try {
      const response = await fetch(`${API_BASE_URL}/processing_millingplan`);
      const data = await response.json();
      console.log('ProcessingBottomBar: Milling plan data loaded:', data);
      setMillingPlanData(data);
    } catch (error) {
      console.error('Error loading milling plan data:', error);
    } finally {
      setLoading(prev => ({ ...prev, millingPlan: false }));
    }
  };

  // Data processing functions
  const processCrushingDataForCharts = (data) => {
    if (!data || data.length === 0) return [];
    
    return data
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map(item => ({
        date: formatDateForChart(item.DateReported),
        tonnesCrushedDS: parseFloat(item.TonnesCrushed_DS) || 0,
        tonnesCrushedNS: parseFloat(item.TonnesCrushed_NS) || 0,
        tonnesCrushedDaily: parseFloat(item.TonnesCrushed_Daily) || 0
      }));
  };

  const processMillDataForCharts = (data) => {
    if (!data || data.length === 0) return [];
    
    return data
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map(item => ({
        date: formatDateForChart(item.DateReported),
        tonnesMilledDS: parseFloat(item.TonnesMilled_DS) || 0,
        tonnesMilledNS: parseFloat(item.TonnesMilled_NS) || 0,
        tonnesMilledDaily: parseFloat(item.TonnesMilled_Daily) || 0
      }));
  };

  const processGoldProducedDataForCharts = (data) => {
    if (!data || data.length === 0) return [];
    
    return data
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map(item => ({
        date: formatDateForChart(item.DateReported),
        bullionWeight: parseFloat(item.BullionWeight) || 0,
        goldSmelted: parseFloat(item.GoldSmelted) || 0
      }));
  };

  // Process daily vs plan data for crusher
  const processCrusherDailyVsPlan = (actualData, planData) => {
    if (!actualData || !planData || actualData.length === 0 || planData.length === 0) return [];
    
    return actualData
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map((item, index) => {
        const planItem = planData[index] || {};
        return {
          date: formatDateForChart(item.DateReported),
          actual: parseFloat(item.TonnesCrushed_Daily) || 0,
          plan: parseFloat(planItem.TonnesCrushed) || 0
        };
      });
  };

  // Process cumulative vs cumulative plan data for crusher
  const processCrusherCumulativeVsPlan = (actualData, planData) => {
    if (!actualData || !planData || actualData.length === 0 || planData.length === 0) return [];
    
    let actualCumulative = 0;
    let planCumulative = 0;
    
    return actualData
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map((item, index) => {
        actualCumulative += parseFloat(item.TonnesCrushed_Daily) || 0;
        planCumulative += parseFloat(planData[index]?.TonnesCrushed) || 0;
        
        return {
          date: formatDateForChart(item.DateReported),
          actualCumulative,
          planCumulative
        };
      });
  };

  // Process daily vs plan data for mills
  const processMillDailyVsPlan = (actualData, planData, millType) => {
    if (!actualData || !planData || actualData.length === 0 || planData.length === 0) return [];
    
    return actualData
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map((item, index) => {
        const planItem = planData[index] || {};
        const planKey = millType === 'mill1' ? 'Mill1_TonnesMilled' : 
                       millType === 'mill3' ? 'Mill3_TonnesMilled' : 'Mill4_TonnesMilled';
        
        return {
          date: formatDateForChart(item.DateReported),
          actual: parseFloat(item.TonnesMilled_Daily) || 0,
          plan: parseFloat(planItem[planKey]) || 0
        };
      });
  };

  // Process cumulative vs cumulative plan data for mills
  const processMillCumulativeVsPlan = (actualData, planData, millType) => {
    if (!actualData || !planData || actualData.length === 0 || planData.length === 0) return [];
    
    let actualCumulative = 0;
    let planCumulative = 0;
    
    return actualData
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map((item, index) => {
        actualCumulative += parseFloat(item.TonnesMilled_Daily) || 0;
        const planKey = millType === 'mill1' ? 'Mill1_TonnesMilled' : 
                       millType === 'mill3' ? 'Mill3_TonnesMilled' : 'Mill4_TonnesMilled';
        planCumulative += parseFloat(planData[index]?.[planKey]) || 0;
        
        return {
          date: formatDateForChart(item.DateReported),
          actualCumulative,
          planCumulative
        };
      });
  };

  // Process downtime data for charts
  const processDowntimeDataForCharts = (data) => {
    if (!data || data.length === 0) return [];
    
    return data
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map(item => ({
        date: formatDateForChart(item.DateReported),
        crusherDowntime: parseFloat(item.Crusher_TotalDownTime_Daily) || 0,
        crusherAvailability: parseFloat(item.Crusher_Availability_Daily) || 0,
        crusherUtilization: parseFloat(item.Crusher_Utilization_Daily) || 0,
        mill1Downtime: parseFloat(item.Mill1_TotalDownTime_Daily) || 0,
        mill1Availability: parseFloat(item.Mill1_Availability_Daily) || 0,
        mill1Utilization: parseFloat(item.Mill1_Utilization_Daily) || 0,
        mill3Downtime: parseFloat(item.Mill3_TotalDownTime_Daily) || 0,
        mill3Availability: parseFloat(item.Mill3_Availability_Daily) || 0,
        mill3Utilization: parseFloat(item.Mill3_Utilization_Daily) || 0,
        mill4Downtime: parseFloat(item.Mill4_TotalDownTime_Daily) || 0,
        mill4Availability: parseFloat(item.Mill4_Availability_Daily) || 0,
        mill4Utilization: parseFloat(item.Mill4_Utilization_Daily) || 0
      }));
  };

  // Process downtime vs plan data (for availability)
  const processDowntimeVsPlan = (actualData, planData, equipmentType) => {
    if (!actualData || !planData || actualData.length === 0 || planData.length === 0) return [];
    
    return actualData
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map((item, index) => {
        const planItem = planData[index] || {};
        let actualDowntime, planDowntime, actualAvailability, planAvailability;
        
        if (equipmentType === 'crusher') {
          actualDowntime = Math.round(parseFloat(item.Crusher_TotalDownTime_Daily) || 0);
          actualAvailability = Math.round((parseFloat(item.Crusher_Availability_Daily) || 0));
          planDowntime = Math.round(parseFloat(planItem.Crusher_TotalDownTime_Daily) || 0);
          planAvailability = Math.round((parseFloat(planItem.Crusher_Availability) || 0) * 100);
        } else if (equipmentType === 'mill1') {
          actualDowntime = Math.round(parseFloat(item.Mill1_TotalDownTime_Daily) || 0);
          actualAvailability = Math.round((parseFloat(item.Mill1_Availability_Daily) || 0) );
          planDowntime = Math.round(parseFloat(planItem.Mill1_TotalDownTime_Daily) || 0);
          planAvailability = Math.round((parseFloat(planItem.Mill1_Availability) || 0) * 100);
        } else if (equipmentType === 'mill3') {
          actualDowntime = Math.round(parseFloat(item.Mill3_TotalDownTime_Daily) || 0);
          actualAvailability = Math.round((parseFloat(item.Mill3_Availability_Daily) || 0));
          planDowntime = Math.round(parseFloat(planItem.Mill3_TotalDownTime_Daily) || 0);
          planAvailability = Math.round((parseFloat(planItem.Mill3_Availability) || 0) * 100);
        } else if (equipmentType === 'mill4') {
          actualDowntime = Math.round(parseFloat(item.Mill4_TotalDownTime_Daily) || 0);
          actualAvailability = Math.round((parseFloat(item.Mill4_Availability_Daily) || 0));
          planDowntime = Math.round(parseFloat(planItem.Mill4_TotalDownTime_Daily) || 0);
          planAvailability = Math.round((parseFloat(planItem.Mill4_Availability) || 0) * 100);
        }
        
        return {
          date: formatDateForChart(item.DateReported),
          actualDowntime,
          planDowntime,
          actualAvailability,
          planAvailability
        };
      });
  };

  // Process effective running time vs plan data
  const processEffectiveRunningTimeVsPlan = (actualData, planData, equipmentType) => {
    if (!actualData || !planData || actualData.length === 0 || planData.length === 0) return [];
    
    return actualData
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map((item, index) => {
        const planItem = planData[index] || {};
        let actualRunningTime, planRunningTime;
        
        // Calculate running time from downtime (24 - downtime hours)
        if (equipmentType === 'crusher') {
          const downtime = parseFloat(item.Crusher_TotalDownTime_Daily) || 0;
          actualRunningTime = Math.round(24 - downtime);
          planRunningTime = Math.round(parseFloat(planItem.Crusher_EffectiveRunningTime) || 0);
        } else if (equipmentType === 'mill1') {
          const downtime = parseFloat(item.Mill1_TotalDownTime_Daily) || 0;
          actualRunningTime = Math.round(24 - downtime);
          planRunningTime = Math.round(parseFloat(planItem.Mill1_EffectiveRunningTime) || 0);
        } else if (equipmentType === 'mill3') {
          const downtime = parseFloat(item.Mill3_TotalDownTime_Daily) || 0;
          actualRunningTime = Math.round(24 - downtime);
          planRunningTime = Math.round(parseFloat(planItem.Mill3_EffectiveRunningTime) || 0);
        } else if (equipmentType === 'mill4') {
          const downtime = parseFloat(item.Mill4_TotalDownTime_Daily) || 0;
          actualRunningTime = Math.round(24 - downtime);
          planRunningTime = Math.round(parseFloat(planItem.Mill4_EffectiveRunningTime) || 0);
        }
        
        return {
          date: formatDateForChart(item.DateReported),
          actualRunningTime,
          planRunningTime
        };
      });
  };

  // Process utilization vs plan data
  const processUtilizationVsPlan = (actualData, planData, equipmentType) => {
    if (!actualData || !planData || actualData.length === 0 || planData.length === 0) return [];
    
    return actualData
      .sort((a, b) => new Date(a.DateReported) - new Date(b.DateReported))
      .map((item, index) => {
        const planItem = planData[index] || {};
        let actualUtilization, planUtilization;
        
        if (equipmentType === 'crusher') {
          actualUtilization = Math.round((parseFloat(item.Crusher_Utilization_Daily) || 0));
          planUtilization = Math.round((parseFloat(planItem.Crusher_Utilization) || 0) * 100);
        } else if (equipmentType === 'mill1') {
          actualUtilization = Math.round((parseFloat(item.Mill1_Utilization_Daily) || 0) );
          planUtilization = Math.round((parseFloat(planItem.Mill1_Utilization) || 0) * 100);
        } else if (equipmentType === 'mill3') {
          actualUtilization = Math.round((parseFloat(item.Mill3_Utilization_Daily) || 0));
          planUtilization = Math.round((parseFloat(planItem.Mill3_Utilization) || 0) * 100);
        } else if (equipmentType === 'mill4') {
          actualUtilization = Math.round((parseFloat(item.Mill4_Utilization_Daily) || 0) );
          planUtilization = Math.round((parseFloat(planItem.Mill4_Utilization) || 0) * 100);
        }
        
        return {
          date: formatDateForChart(item.DateReported),
          actualUtilization,
          planUtilization
        };
      });
  };

  const renderFiltersTab = () => {
    return (
      <div className="filters-container">
        <div className="filters-header">
          <h3>Data Filters</h3>
          <p>Set date range to filter all processing data</p>
        </div>
        
        <div className="date-filters">
          <div className="date-range-section">
            <div className="date-input-group">
              <label htmlFor="fromDate">From Date:</label>
              <input
                type="date"
                id="fromDate"
                value={fromDate}
                onChange={(e) => handleDateChange('fromDate', e.target.value)}
                className="date-input"
              />
            </div>
            
            <div className="date-input-group">
              <label htmlFor="toDate">To Date:</label>
              <input
                type="date"
                id="toDate"
                value={toDate}
                onChange={(e) => handleDateChange('toDate', e.target.value)}
                className="date-input"
              />
            </div>
          </div>
          
          <div className="quick-date-buttons">
            <h4>Quick Date Selection:</h4>
            <div className="date-buttons">
              <button 
                className={`date-btn ${activeDateRange === 'yesterday' ? 'active' : ''}`}
                onClick={() => setDateRange('yesterday')}
              >
                Yesterday
              </button>
              <button 
                className={`date-btn ${activeDateRange === 'thisWeek' ? 'active' : ''}`}
                onClick={() => setDateRange('thisWeek')}
              >
                This Week
              </button>
              <button 
                className={`date-btn ${activeDateRange === 'thisMonth' ? 'active' : ''}`}
                onClick={() => setDateRange('thisMonth')}
              >
                This Month
              </button>
              <button 
                className={`date-btn ${activeDateRange === 'thisYear' ? 'active' : ''}`}
                onClick={() => setDateRange('thisYear')}
              >
                This Year
              </button>
            </div>
          </div>
        </div>
        
        <div className="filter-summary">
          <h4>Filter Results:</h4>
          <div className="filter-stats">
                  <div className="stat-item">
                    <span className="stat-label">Crushing Records:</span>
                    <span className="stat-value">{filteredData.crushing?.length || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Mill1 Records:</span>
                    <span className="stat-value">{filteredData.mill1?.length || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Mill3 Records:</span>
                    <span className="stat-value">{filteredData.mill3?.length || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Mill4 Records:</span>
                    <span className="stat-value">{filteredData.mill4?.length || 0}</span>
                  </div>
            <div className="stat-item">
              <span className="stat-label">Gold Produced:</span>
              <span className="stat-value">{filteredData.goldProduced?.length || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Downtime Records:</span>
              <span className="stat-value">{filteredData.downtime?.length || 0}</span>
            </div>
                  <div className="stat-item">
                    <span className="stat-label">Comments:</span>
                    <span className="stat-value">{filteredData.comments?.length || 0}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Milling Plan:</span>
                    <span className="stat-value">{filteredData.millingPlan?.length || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        };

  const renderSummaryTab = () => {
    const summaryData = [
      { name: 'Crusher', value: filteredData.crushing?.length || 0, color: '#8884d8' },
      { name: 'Mill1', value: filteredData.mill1?.length || 0, color: '#82ca9d' },
      { name: 'Mill3', value: filteredData.mill3?.length || 0, color: '#ffc658' },
      { name: 'Mill4', value: filteredData.mill4?.length || 0, color: '#ff7300' },
      { name: 'Gold Produced', value: filteredData.goldProduced?.length || 0, color: '#ffa400' }
    ];

    return (
      <div className="summary-tab">
        <h3>Processing Operations Summary</h3>
        <div className="summary-stats">
          <div className="stat-card">
            <h4>Crushing Records</h4>
            <p>{filteredData.crushing.length}</p>
          </div>
          <div className="stat-card">
            <h4>Mill1 Records</h4>
            <p>{filteredData.mill1.length}</p>
          </div>
          <div className="stat-card">
            <h4>Mill3 Records</h4>
            <p>{filteredData.mill3.length}</p>
          </div>
          <div className="stat-card">
            <h4>Mill4 Records</h4>
            <p>{filteredData.mill4.length}</p>
          </div>
          <div className="stat-card">
            <h4>Gold Produced Records</h4>
            <p>{filteredData.goldProduced.length}</p>
          </div>
        </div>
        
        <div className="summary-chart">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={summaryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {summaryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderCrusherTab = () => {
    const chartData = processCrushingDataForCharts(filteredData.crushing);
    const dailyVsPlanData = processCrusherDailyVsPlan(filteredData.crushing, millingPlanData);
    const cumulativeVsPlanData = processCrusherCumulativeVsPlan(filteredData.crushing, millingPlanData);
    
    return (
      <div className="crusher-tab">
        <h3>Crusher Performance</h3>
        {loading.crushing ? (
          <div className="loading">Loading crushing data...</div>
        ) : (
          <div className="charts-container">
            {/* Daily vs Plan Chart */}
            <div className="chart-section">
              <h4>Daily vs Plan</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyVsPlanData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="actual" stroke="#ff6b6b" name="Actual" />
                  <Line type="monotone" dataKey="plan" stroke="#4ecdc4" name="Plan" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Cumulative vs Cumulative Plan Chart */}
            <div className="chart-section">
              <h4>Cumulative vs Cumulative Plan</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={cumulativeVsPlanData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="actualCumulative" stroke="#ff6b6b" name="Actual Cumulative" />
                  <Line type="monotone" dataKey="planCumulative" stroke="#4ecdc4" name="Plan Cumulative" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Daily, NS, DS Chart */}
            <div className="chart-section">
              <h4>Daily, NS, DS Performance</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="tonnesCrushedDS" stroke="#8884d8" name="Tonnes Crushed DS" />
                  <Line type="monotone" dataKey="tonnesCrushedNS" stroke="#82ca9d" name="Tonnes Crushed NS" />
                  <Line type="monotone" dataKey="tonnesCrushedDaily" stroke="#ffc658" name="Tonnes Crushed Daily" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMill1Tab = () => {
    const chartData = processMillDataForCharts(filteredData.mill1);
    const dailyVsPlanData = processMillDailyVsPlan(filteredData.mill1, millingPlanData, 'mill1');
    const cumulativeVsPlanData = processMillCumulativeVsPlan(filteredData.mill1, millingPlanData, 'mill1');
    
    return (
      <div className="mill1-tab">
        <h3>Mill1 Performance</h3>
        {loading.mill1 ? (
          <div className="loading">Loading mill1 data...</div>
        ) : (
          <div className="charts-container">
            {/* Daily vs Plan Chart */}
            <div className="chart-section">
              <h4>Daily vs Plan</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyVsPlanData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="actual" stroke="#ff6b6b" name="Actual" />
                  <Line type="monotone" dataKey="plan" stroke="#4ecdc4" name="Plan" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Cumulative vs Cumulative Plan Chart */}
            <div className="chart-section">
              <h4>Cumulative vs Cumulative Plan</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={cumulativeVsPlanData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="actualCumulative" stroke="#ff6b6b" name="Actual Cumulative" />
                  <Line type="monotone" dataKey="planCumulative" stroke="#4ecdc4" name="Plan Cumulative" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Daily, NS, DS Chart */}
            <div className="chart-section">
              <h4>Daily, NS, DS Performance</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="tonnesMilledDS" stroke="#8884d8" name="Tonnes Milled DS" />
                  <Line type="monotone" dataKey="tonnesMilledNS" stroke="#82ca9d" name="Tonnes Milled NS" />
                  <Line type="monotone" dataKey="tonnesMilledDaily" stroke="#ffc658" name="Tonnes Milled Daily" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMill4Tab = () => {
    const chartData = processMillDataForCharts(filteredData.mill4);
    const dailyVsPlanData = processMillDailyVsPlan(filteredData.mill4, millingPlanData, 'mill4');
    const cumulativeVsPlanData = processMillCumulativeVsPlan(filteredData.mill4, millingPlanData, 'mill4');
    
    return (
      <div className="mill4-tab">
        <h3>Mill4 Performance</h3>
        {loading.mill4 ? (
          <div className="loading">Loading mill4 data...</div>
        ) : (
          <div className="charts-container">
            {/* Daily vs Plan Chart */}
            <div className="chart-section">
              <h4>Daily vs Plan</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyVsPlanData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="actual" stroke="#ff6b6b" name="Actual" />
                  <Line type="monotone" dataKey="plan" stroke="#4ecdc4" name="Plan" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Cumulative vs Cumulative Plan Chart */}
            <div className="chart-section">
              <h4>Cumulative vs Cumulative Plan</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={cumulativeVsPlanData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="actualCumulative" stroke="#ff6b6b" name="Actual Cumulative" />
                  <Line type="monotone" dataKey="planCumulative" stroke="#4ecdc4" name="Plan Cumulative" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Daily, NS, DS Chart */}
            <div className="chart-section">
              <h4>Daily, NS, DS Performance</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="tonnesMilledDS" stroke="#8884d8" name="Tonnes Milled DS" />
                  <Line type="monotone" dataKey="tonnesMilledNS" stroke="#82ca9d" name="Tonnes Milled NS" />
                  <Line type="monotone" dataKey="tonnesMilledDaily" stroke="#ffc658" name="Tonnes Milled Daily" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMill3Tab = () => {
    const chartData = processMillDataForCharts(filteredData.mill3);
    const dailyVsPlanData = processMillDailyVsPlan(filteredData.mill3, millingPlanData, 'mill3');
    const cumulativeVsPlanData = processMillCumulativeVsPlan(filteredData.mill3, millingPlanData, 'mill3');
    
    return (
      <div className="mill3-tab">
        <h3>Mill3 Performance</h3>
        {loading.mill3 ? (
          <div className="loading">Loading mill3 data...</div>
        ) : (
          <div className="charts-container">
            {/* Daily vs Plan Chart */}
            <div className="chart-section">
              <h4>Daily vs Plan</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailyVsPlanData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="actual" stroke="#ff6b6b" name="Actual" />
                  <Line type="monotone" dataKey="plan" stroke="#4ecdc4" name="Plan" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Cumulative vs Cumulative Plan Chart */}
            <div className="chart-section">
              <h4>Cumulative vs Cumulative Plan</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={cumulativeVsPlanData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="actualCumulative" stroke="#ff6b6b" name="Actual Cumulative" />
                  <Line type="monotone" dataKey="planCumulative" stroke="#4ecdc4" name="Plan Cumulative" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Daily, NS, DS Chart */}
            <div className="chart-section">
              <h4>Daily, NS, DS Performance</h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <RechartsLegend />
                  <Line type="monotone" dataKey="tonnesMilledDS" stroke="#8884d8" name="Tonnes Milled DS" />
                  <Line type="monotone" dataKey="tonnesMilledNS" stroke="#82ca9d" name="Tonnes Milled NS" />
                  <Line type="monotone" dataKey="tonnesMilledDaily" stroke="#ffc658" name="Tonnes Milled Daily" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDowntimeTab = () => {
    const chartData = processDowntimeDataForCharts(filteredData.downtime);
    
    // Effective Running Time data
    const crusherRunningTime = processEffectiveRunningTimeVsPlan(filteredData.downtime, millingPlanData, 'crusher');
    const mill1RunningTime = processEffectiveRunningTimeVsPlan(filteredData.downtime, millingPlanData, 'mill1');
    const mill3RunningTime = processEffectiveRunningTimeVsPlan(filteredData.downtime, millingPlanData, 'mill3');
    const mill4RunningTime = processEffectiveRunningTimeVsPlan(filteredData.downtime, millingPlanData, 'mill4');
    
    // Availability data
    const crusherAvailability = processDowntimeVsPlan(filteredData.downtime, millingPlanData, 'crusher');
    const mill1Availability = processDowntimeVsPlan(filteredData.downtime, millingPlanData, 'mill1');
    const mill3Availability = processDowntimeVsPlan(filteredData.downtime, millingPlanData, 'mill3');
    const mill4Availability = processDowntimeVsPlan(filteredData.downtime, millingPlanData, 'mill4');
    
    // Utilization data
    const crusherUtilization = processUtilizationVsPlan(filteredData.downtime, millingPlanData, 'crusher');
    const mill1Utilization = processUtilizationVsPlan(filteredData.downtime, millingPlanData, 'mill1');
    const mill3Utilization = processUtilizationVsPlan(filteredData.downtime, millingPlanData, 'mill3');
    const mill4Utilization = processUtilizationVsPlan(filteredData.downtime, millingPlanData, 'mill4');
    
    return (
      <div className="downtime-tab">
        <h3>Equipment Performance Metrics</h3>
        {loading.downtime || loading.millingPlan ? (
          <div className="loading">Loading downtime and plan data...</div>
        ) : (
          <div className="charts-container">
            {/* Effective Running Time Section */}
            <div className="metric-section">
              <div className="section-header" onClick={() => toggleSection('effectiveRunningTime')}>
                <h2>Effective Running Time (Hours)</h2>
                <span className={`collapse-arrow ${collapsedSections.effectiveRunningTime ? 'collapsed' : 'expanded'}`}>
                  ▼
                </span>
              </div>
              
              {!collapsedSections.effectiveRunningTime && (
                <div className="section-content">
                  <div className="chart-section">
                    <h4>Crusher Effective Running Time vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={crusherRunningTime}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualRunningTime" stroke="#ff6b6b" name="Actual Running Time" />
                        <Line type="monotone" dataKey="planRunningTime" stroke="#4ecdc4" name="Plan Running Time" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-section">
                    <h4>Mill1 Effective Running Time vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={mill1RunningTime}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualRunningTime" stroke="#ff6b6b" name="Actual Running Time" />
                        <Line type="monotone" dataKey="planRunningTime" stroke="#4ecdc4" name="Plan Running Time" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-section">
                    <h4>Mill3 Effective Running Time vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={mill3RunningTime}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualRunningTime" stroke="#ff6b6b" name="Actual Running Time" />
                        <Line type="monotone" dataKey="planRunningTime" stroke="#4ecdc4" name="Plan Running Time" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-section">
                    <h4>Mill4 Effective Running Time vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={mill4RunningTime}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualRunningTime" stroke="#ff6b6b" name="Actual Running Time" />
                        <Line type="monotone" dataKey="planRunningTime" stroke="#4ecdc4" name="Plan Running Time" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Availability Section */}
            <div className="metric-section">
              <div className="section-header" onClick={() => toggleSection('availability')}>
                <h2>Availability (%)</h2>
                <span className={`collapse-arrow ${collapsedSections.availability ? 'collapsed' : 'expanded'}`}>
                  ▼
                </span>
              </div>
              
              {!collapsedSections.availability && (
                <div className="section-content">
                  <div className="chart-section">
                    <h4>Crusher Availability vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={crusherAvailability}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualAvailability" stroke="#82ca9d" name="Actual Availability" />
                        <Line type="monotone" dataKey="planAvailability" stroke="#ffc658" name="Plan Availability" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-section">
                    <h4>Mill1 Availability vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={mill1Availability}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualAvailability" stroke="#82ca9d" name="Actual Availability" />
                        <Line type="monotone" dataKey="planAvailability" stroke="#ffc658" name="Plan Availability" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-section">
                    <h4>Mill3 Availability vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={mill3Availability}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualAvailability" stroke="#82ca9d" name="Actual Availability" />
                        <Line type="monotone" dataKey="planAvailability" stroke="#ffc658" name="Plan Availability" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-section">
                    <h4>Mill4 Availability vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={mill4Availability}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualAvailability" stroke="#82ca9d" name="Actual Availability" />
                        <Line type="monotone" dataKey="planAvailability" stroke="#ffc658" name="Plan Availability" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>

            {/* Utilization Section */}
            <div className="metric-section">
              <div className="section-header" onClick={() => toggleSection('utilization')}>
                <h2>Utilization (%)</h2>
                <span className={`collapse-arrow ${collapsedSections.utilization ? 'collapsed' : 'expanded'}`}>
                  ▼
                </span>
              </div>
              
              {!collapsedSections.utilization && (
                <div className="section-content">
                  <div className="chart-section">
                    <h4>Crusher Utilization vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={crusherUtilization}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualUtilization" stroke="#8884d8" name="Actual Utilization" />
                        <Line type="monotone" dataKey="planUtilization" stroke="#ff7300" name="Plan Utilization" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-section">
                    <h4>Mill1 Utilization vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={mill1Utilization}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualUtilization" stroke="#8884d8" name="Actual Utilization" />
                        <Line type="monotone" dataKey="planUtilization" stroke="#ff7300" name="Plan Utilization" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-section">
                    <h4>Mill3 Utilization vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={mill3Utilization}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualUtilization" stroke="#8884d8" name="Actual Utilization" />
                        <Line type="monotone" dataKey="planUtilization" stroke="#ff7300" name="Plan Utilization" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="chart-section">
                    <h4>Mill4 Utilization vs Plan</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={mill4Utilization}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <RechartsLegend />
                        <Line type="monotone" dataKey="actualUtilization" stroke="#8884d8" name="Actual Utilization" />
                        <Line type="monotone" dataKey="planUtilization" stroke="#ff7300" name="Plan Utilization" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderGoldProducedTab = () => {
    const chartData = processGoldProducedDataForCharts(filteredData.goldProduced);
    
    return (
      <div className="gold-produced-tab">
        <h3>Gold Production</h3>
        {loading.goldProduced ? (
          <div className="loading">Loading gold produced data...</div>
        ) : (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <RechartsTooltip content={<CustomTooltip />} />
                <RechartsLegend />
                <Bar dataKey="bullionWeight" fill="#ffa400" name="Bullion Weight" />
                <Bar dataKey="goldSmelted" fill="#ff7300" name="Gold Smelted" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`bottomContainer${expanded ? ' expanded' : ''}`}>
      {/* Tab Selector and Control Buttons Row */}
      <div className="top-controls-row">
        <div className="bottomBarTabSelector">
          {PROCESSING_TAB_OPTIONS.map(tab => (
            <button
              key={tab.key}
              className={`bottomBarTabBtn${selectedTab === tab.key ? ' active' : ''}`}
              onClick={() => setSelectedTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {/*  */}
      </div>
      {/* Tab Content */}
      {selectedTab === 'filters' && (
        <div className="bottomBarTabContent">
          {renderFiltersTab()}
        </div>
      )}
      {selectedTab === 'summary' && (
        <div className="bottomBarTabContent">
          {renderSummaryTab()}
        </div>
      )}
      {selectedTab === 'crusher' && (
        <div className="bottomBarTabContent">
          {renderCrusherTab()}
        </div>
      )}
      {selectedTab === 'mill1' && (
        <div className="bottomBarTabContent">
          {renderMill1Tab()}
        </div>
      )}
      {selectedTab === 'mill3' && (
        <div className="bottomBarTabContent">
          {renderMill3Tab()}
        </div>
      )}
      {selectedTab === 'mill4' && (
        <div className="bottomBarTabContent">
          {renderMill4Tab()}
        </div>
      )}
      {selectedTab === 'downtime' && (
        <div className="bottomBarTabContent">
          {renderDowntimeTab()}
        </div>
      )}
      {selectedTab === 'goldproduced' && (
        <div className="bottomBarTabContent">
          {renderGoldProducedTab()}
        </div>
      )}
    </div>
  );
};

export default ProcessingBottomBar;
